import { startCase } from 'lodash';

import type { QBFilter, QBFilterOptions } from '@/components/QueryBuilder';
import {
  PRIORITY_COLORS,
  STATUS_COLORS
} from '@/constants/chartcolors.constants';
import { PONDURANCE_NAME } from '@/constants/common';
import type { SearchFilters } from '@/features/SearchBar/SearchBar.types';
import calcGroupBy from '@/utils/Common.utils';

import { TICKET_THREAT_RESOLUTIONS } from '../Ticket/Ticket.constants';
import { TICKET_STATUS } from './Tickets.constants';
import type { Ticket, TicketDTO } from './Tickets.types';

type GroupByColorResolver = (label: string, index: number) => string;

export const mapGroupByOptionsToDonutData = (
  groupByData: QBFilterOptions[] | undefined,
  colorResolver: GroupByColorResolver
) => {
  const chartData = groupByData || [];
  const total = chartData.reduce((acc, item) => acc + (item?.count || 0), 0);

  return chartData.map((item, index) => ({
    ...item,
    count: item.count,
    name: item.label,
    color: colorResolver(item.label, index),
    legendLabel: `${item.label} (${(((item?.count || 0) / total) * 100).toFixed(1)}%)`
  }));
};

export const mapTicketsDTO = (tickets: TicketDTO[]): Ticket[] => {
  return tickets.map((ticket) => ({
    ...ticket,
    category_name: ticket?.ticket_category?.name || '',
    next_action_owner: startCase(ticket.next_action || ''),
    organization_author: ticket.user.internal
      ? PONDURANCE_NAME
      : ticket.organization?.name || '',
    organization_name: ticket.organization?.name || '',
    owner_name: ticket.owner?.name || '',
    priority_name: ticket.ticket_priority?.name || '',
    resolution_name:
      TICKET_THREAT_RESOLUTIONS.find((item) => ticket.resolution_id === item.id)
        ?.text || '',
    status_name: ticket.ticket_status?.name || '',
    team_queue: ticket.team_queue || '',
    type_name: ticket.ticket_type === 1 ? 'Threat' : 'Service'
  }));
};

export const formatTickets = (
  filteredTickets: TicketDTO[],
  filterAttribute: keyof TicketDTO
) => {
  let results = calcGroupBy(filteredTickets, filterAttribute);

  results = results.map((result) => ({
    ...result,
    color: filterAttribute.includes('ticket_priority')
      ? PRIORITY_COLORS[(result.name as keyof typeof PRIORITY_COLORS) || 'P3']
      : STATUS_COLORS[
          (result.name as keyof typeof STATUS_COLORS) || TICKET_STATUS.OPEN
        ]
  }));

  return results;
};

export const normalizeCustomerTicketAuthorFilters = (
  filters: QBFilter[],
  userIsInternal: boolean
) => {
  if (userIsInternal) return filters;

  return filters.flatMap((filter) => {
    if (filter.field !== 'user__name') return [filter];

    const names = String(filter.value)
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean);

    if (!names.includes(PONDURANCE_NAME) || names.length <= 1) {
      return [filter];
    }

    const normalizedNames = names.filter((name) => name !== PONDURANCE_NAME);

    if (normalizedNames.length === 0) {
      return [];
    }

    return [{ ...filter, value: normalizedNames.join(',') }];
  });
};

export const formatTicketFilters = (filters?: SearchFilters) => {
  if (!filters) return {};
  const finalFilters = {} as Record<
    string,
    string | number | string[] | number[]
  >;
  Object.entries(filters).forEach(([key, value]) => {
    // For type_name, if value is greater than 1 it means both types are selected and filtering is not needed
    if (key === 'type_name' && value.length === 1) {
      const typeNumber = value[0] === 'Threat' ? 1 : 2;
      finalFilters.ticket_type = typeNumber;
    } else if (key === 'status_name') {
      finalFilters.ticket_status__name__in = value;
    } else if (key === 'priority_name') {
      finalFilters.ticket_priority__name__in = value;
    } else {
      finalFilters[key] = value;
    }
  });
  return finalFilters;
};

export default mapTicketsDTO;
